// Custom hook for contact management
import { useState, useEffect } from 'react';
import { contactService, ContactErrorCode, type ContactServiceError } from '../../backend/services/contacts';
import type { 
  Contact,
  ContactWithActivity,
  CreateContactData, 
  UpdateContactData, 
  ContactsFilter,
  ContactStats
} from '../../shared/types/entities';

// Toast notification hook (simple implementation)
const useToast = () => {
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: 'success' | 'error' | 'info' }>>([]);
  
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  };
  
  return { toasts, showToast };
};
export const useContacts = (filter: ContactsFilter = {}) => {
  const [contacts, setContacts] = useState<ContactWithActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  const fetchContacts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await contactService.getContacts(filter);
      setContacts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch contacts');
      showToast('Failed to load contacts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const createContact = async (contactData: CreateContactData): Promise<ContactWithActivity | null> => {
    try {
      setError(null);
      showToast('Creating contact...', 'info');
      
      const result = await contactService.createContact(contactData);
      
      if (result.success) {
        const newContact = { ...result.data, artists_count: 0, campaigns_count: 0, total_budget: 0, has_activity: false };
        setContacts(prev => [newContact, ...prev]);
        showToast('Contact created successfully', 'success');
        return newContact;
      } else {
        const errorMessage = result.error.code === ContactErrorCode.DUPLICATE_EMAIL 
          ? 'A contact with this email already exists'
          : result.error.message;
        setError(errorMessage);
        showToast(errorMessage, 'error');
        return null;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create contact';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      return null;
    }
  };

  const updateContact = async (id: string, updates: UpdateContactData, options?: { optimistic?: boolean }): Promise<ContactWithActivity | null> => {
    try {
      setError(null);
      
      // Optimistic update
      if (options?.optimistic) {
        setContacts(prev => prev.map(c => 
          c.id === id ? { ...c, ...updates } : c
        ));
      }
      
      const result = await contactService.updateContact(id, updates);
      
      if (result.success) {
        const updatedContact = { 
          ...result.data, 
          artists_count: contacts.find(c => c.id === id)?.artists_count || 0,
          campaigns_count: contacts.find(c => c.id === id)?.campaigns_count || 0,
          total_budget: contacts.find(c => c.id === id)?.total_budget || 0,
          has_activity: contacts.find(c => c.id === id)?.has_activity || false
        };
        setContacts(prev => prev.map(c => c.id === id ? updatedContact : c));
        showToast('Contact updated successfully', 'success');
        return updatedContact;
      } else {
        // Revert optimistic update on error
        if (options?.optimistic) {
          await fetchContacts(); // Refresh to get original data
        }
        const errorMessage = result.error.code === ContactErrorCode.DUPLICATE_EMAIL 
          ? 'A contact with this email already exists'
          : result.error.message;
        setError(errorMessage);
        showToast(errorMessage, 'error');
        return null;
      }
    } catch (err) {
      // Revert optimistic update on error
      if (options?.optimistic) {
        await fetchContacts(); // Refresh to get original data
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to update contact';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      return null;
    }
  };

  const deleteContact = async (id: string): Promise<void> => {
    try {
      setError(null);
      showToast('Deleting contact...', 'info');
      await contactService.deleteContact(id);
      setContacts(prev => prev.filter(c => c.id !== id));
      showToast('Contact deleted successfully', 'success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete contact';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const batchDeleteContacts = async (ids: string[]): Promise<void> => {
    try {
      setError(null);
      showToast(`Deleting ${ids.length} contacts...`, 'info');
      
      const result = await contactService.batchDeleteContacts(ids);
      
      // Remove successfully deleted contacts
      setContacts(prev => prev.filter(c => !result.success.includes(c.id)));
      
      if (result.success.length > 0) {
        showToast(`Successfully deleted ${result.success.length} contacts`, 'success');
      }
      
      if (result.failed.length > 0) {
        showToast(`Failed to delete ${result.failed.length} contacts`, 'error');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete contacts';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const exportContacts = async (ids?: string[]): Promise<void> => {
    try {
      showToast('Exporting contacts...', 'info');
      const csvContent = await contactService.exportContacts(ids);
      
      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('Contacts exported successfully', 'success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export contacts';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const getArchivedContacts = async (): Promise<ContactWithActivity[]> => {
    try {
      setError(null);
      const data = await contactService.getArchivedContacts();
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch archived contacts';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      return [];
    }
  };

  const archiveContact = async (id: string): Promise<void> => {
    try {
      setError(null);
      showToast('Archiving contact...', 'info');
      await contactService.archiveContact(id);
      
      // Remove from main list
      setContacts(prev => prev.filter(c => c.id !== id));
      
      showToast('Contact archived successfully', 'success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to archive contact';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };

  const restoreContact = async (id: string): Promise<void> => {
    try {
      setError(null);
      showToast('Restoring contact...', 'info');
      await contactService.restoreContact(id);
      setContacts(prev => prev.filter(c => c.id !== id));
      showToast('Contact restored successfully', 'success');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore contact';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    }
  };
  useEffect(() => {
    fetchContacts();
  }, [JSON.stringify(filter)]);

  return {
    contacts,
    loading,
    error,
    createContact,
    updateContact,
    deleteContact,
    batchDeleteContacts,
    exportContacts,
    getArchivedContacts,
    archiveContact,
    restoreContact,
    refetch: fetchContacts
  };
};

// Hook for single contact
export const useContact = (id: string | undefined) => {
  const [contact, setContact] = useState<Contact | null>(null);
  const [stats, setStats] = useState<ContactStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContact = async () => {
    if (!id) {
      setContact(null);
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [contactData, statsData] = await Promise.all([
        contactService.getContactById(id),
        contactService.getContactStats(id)
      ]);
      
      setContact(contactData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch contact');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContact();
  }, [id]);

  return {
    contact,
    stats,
    loading,
    error,
    refetch: fetchContact
  };
};